import React, { Suspense } from "react";

import { useAllowedMenuPaths } from "@hooks/index";
import LoadingScreen from "@components/LoadingScreen";
import { NotFoundPage, PATHS } from "@/routes";

interface MenuAccessGuardProps {
  /** Path da rota sendo aberta (sem barra inicial) — mesmo valor usado em routes.ts/PATHS. */
  path: string;
  children: React.ReactNode;
}

/**
 * Bloqueia abrir uma tela (via URL direta OU aba) que não apareça na árvore de menu do usuário
 * logado (request 116/`buildMenuTree`) — cobre os dois caminhos que chegam num componente de tela:
 * `AppRouter.tsx` (rota direta) e `TabPageRenderer.tsx` (aba aberta pelo menu lateral). Dashboard é
 * sempre liberado — não vem do request 116, é fixo (`DASHBOARD_NODE`).
 *
 * Mostra a página de "não encontrado" (não um "acesso negado" explícito) quando bloqueia — de
 * propósito: não confirma pra quem está tentando que a rota existe, só que ela não achou nada ali.
 *
 * No GED (SystemCode '61'), diferente de projetos onde este componente é só defesa em profundidade,
 * a resposta do request 116 já passa por `acSecurityManager.Instance.ApplySecurity` no backend
 * (`ucuMain.pas`) — o menu que chega aqui já vem filtrado pela permissão real do usuário. Este guard
 * reflete essa filtragem, não é uma camada extra torcendo pra um dia o backend filtrar de verdade.
 */
const MenuAccessGuard: React.FC<MenuAccessGuardProps> = ({ path, children }) => {
  const { allowedPaths, isLoading } = useAllowedMenuPaths();

  if (path === PATHS.DASHBOARD) return <>{children}</>;
  if (isLoading) return <LoadingScreen />;

  if (!allowedPaths.has(path)) {
    return (
      <Suspense fallback={<LoadingScreen />}>
        <NotFoundPage />
      </Suspense>
    );
  }

  return <>{children}</>;
};

export default MenuAccessGuard;
